// @ts-strict-ignore
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown } from 'react-bootstrap';
import _ from 'lodash';
import { Icon } from '@seeqdev/qomponents';
import { APPSERVER_API_PREFIX } from '@/main/app.constants';
import {
  cancelAllServerRequests as cancelAllServerRequestsUtils,
  cancelCurrentUserServerRequests as cancelCurrentUserServerRequestsUtils,
} from '@/requests/pendingRequests.utilities';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { SupportRequestModal } from '@/supportRequest/SupportRequestModal';
import { ButtonWithPopover } from '@/core/ButtonWithPopover.molecule';
import { successToast, warnToast } from '@/utilities/toast.utilities';
import { sqLicenseManagementStore } from '@/core/core.stores';
import { LICENSE_STATUS } from '@/licenseManagement/licenseManagement.constants.ts';
import { emitAllServerRequestsCanceled } from '@/services/notifier.service';
import { canReadAuditTrail, isAdmin } from '@/services/authorization.service';

interface HamburgerMenuIF {
  toggleFullScreen: () => void;
  initialFullScreenState: boolean;
}

export const HamburgerMenu: React.FunctionComponent<HamburgerMenuIF> = ({
  toggleFullScreen,
  initialFullScreenState,
}) => {
  const [isFullScreen, setIsFullScreen] = useState(initialFullScreenState);
  const [showSupportRequestModal, setShowSupportRequestModal] = useState(false);

  const exitHandler = () => {
    if (
      !document.fullscreenElement &&
      !document['webkitIsFullScreen'] &&
      !document['mozFullScreen'] &&
      !document['msFullscreenElement']
    ) {
      setIsFullScreen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('fullscreenchange', exitHandler);
    document.addEventListener('webkitfullscreenchange', exitHandler);
    document.addEventListener('mozfullscreenchange', exitHandler);
    document.addEventListener('MSFullscreenChange', exitHandler);
    return () => {
      document.removeEventListener('fullscreenchange', exitHandler);
      document.removeEventListener('webkitfullscreenchange', exitHandler);
      document.removeEventListener('mozfullscreenchange', exitHandler);
      document.removeEventListener('MSFullscreenChange', exitHandler);
    };
  }, []);

  const { t } = useTranslation();
  const licenseStatus = useFluxPath(sqLicenseManagementStore, () => sqLicenseManagementStore.licenseStatus);
  const accessibleWithCurrentLicense = !_.includes(
    [LICENSE_STATUS.NONE, LICENSE_STATUS.UNKNOWN, LICENSE_STATUS.EXPIRED],
    licenseStatus,
  );

  const openApiReference = () => {
    // Construct a new URL to the apiReference page using, using the current protocol/host/port
    const swaggerUrl = `/api-docs/?baseUrl=${window.encodeURIComponent(`${APPSERVER_API_PREFIX}/swagger.json`)}`;
    window.open(swaggerUrl);
  };

  const cancelCurrentUserServerRequests = () =>
    cancelCurrentUserServerRequestsUtils()
      .then(() => {
        successToast({ messageKey: 'REQUEST_CANCELLATION.SUCCESS_USER' });
      })
      .catch((error) => {
        warnToast({ messageKey: 'REQUEST_CANCELLATION.FAILURE' });
        return error;
      });

  const cancelAllServerRequests = () =>
    cancelAllServerRequestsUtils()
      .then(emitAllServerRequestsCanceled)
      .then(() => {
        successToast({ messageKey: 'REQUEST_CANCELLATION.SUCCESS_ALL' });
      })
      .catch((error) => {
        warnToast({ messageKey: 'REQUEST_CANCELLATION.FAILURE' });
        return error;
      });

  const openAdminTools = () => {
    window.open('/administration', 'SeeqAdmin');
  };

  const fullScreenAction = () => {
    toggleFullScreen();
    setIsFullScreen(!isFullScreen);
  };

  const renderDropdownEntry = (
    { href = '#', iconClass = '', translationKey = '', action = _.noop, display = false, divider = false, testId = '' },
    index: number,
  ) => {
    if (display) {
      return (
        <Dropdown.Item
          href={href}
          key={`dropdown_${translationKey}`}
          onClick={action}
          target="_blank"
          className="sq-force-text-gray"
          data-testid={testId}>
          <Icon icon={`${iconClass} fa-fw`} type="inherit" />
          <span className="forceFont pl10" data-testid={`moreActions_${translationKey}`}>
            {t(translationKey)}
          </span>
        </Dropdown.Item>
      );
    }

    if (divider) {
      return <div className="dropdown-divider" key={index} />;
    }
  };

  const actions = [
    {
      href: '#',
      iconClass: isFullScreen
        ? 'fa-arrow-down-left-and-arrow-up-right-from-center'
        : 'fa-arrow-up-right-and-arrow-down-left-from-center',
      action: fullScreenAction,
      translationKey: isFullScreen ? 'RESTORE' : 'FULLSCREEN',
      display: true,
      testId: 'fullScreen',
    },
    {
      divider: true,
    },
    {
      href: '#',
      iconClass: 'fa-question-circle',
      translationKey: 'SUPPORT_REQUEST.GET_HELP',
      action: () => setShowSupportRequestModal(true),
      display: accessibleWithCurrentLicense,
      testId: 'supportRequest',
    },
    {
      divider: true,
    },
    {
      href: '#',
      iconClass: 'fa-code',
      translationKey: 'API_REFERENCE',
      action: openApiReference,
      display: accessibleWithCurrentLicense,
      testId: 'apiReference',
    },
    {
      href: '#',
      iconClass: 'fa-lock',
      translationKey: 'ADMINISTRATION',
      action: openAdminTools,
      display: isAdmin() && accessibleWithCurrentLicense,
      testId: 'administration',
    },
    {
      href: 'license',
      iconClass: 'fa-file-zipper',
      translationKey: 'LICENSE.LICENSE_MANAGEMENT',
      display: isAdmin(),
      testId: 'licenseManagement',
    },
    {
      href: 'logs',
      iconClass: 'fa-file-lines',
      translationKey: 'LOGS.VIEW_LOGS',
      display: accessibleWithCurrentLicense,
      testId: 'viewLogs',
    },
    {
      href: 'auditTrail',
      iconClass: 'fas fa-th-list',
      translationKey: 'AUDIT_TRAIL.VIEW_ENTRIES',
      display: canReadAuditTrail(),
      testId: 'viewEntries',
    },
    {
      divider: true,
    },
    {
      href: '#',
      iconClass: 'fa-user-xmark',
      translationKey: 'REQUEST_CANCELLATION.CANCEL_USER',
      action: cancelCurrentUserServerRequests,
      display: true,
      testId: 'requestCancellationUser',
    },
    {
      href: '#',
      iconClass: 'fa-ban',
      translationKey: 'REQUEST_CANCELLATION.CANCEL_ALL',
      action: cancelAllServerRequests,
      display: isAdmin(),
      testId: 'requestCancellationAll',
    },
  ];

  return (
    <>
      <ButtonWithPopover
        popoverConfig={{
          id: 'hamburger-menu',
          placement: 'bottom',
        }}
        label={
          <Icon
            icon="fa-bars"
            extraClassNames="fa-xlg sq-navbar-default flexNoGrowNoShrink pr12"
            type="white"
            testId="hamburgerMenu"
            id="options"
          />
        }>
        <>{_.map(actions, (action, index) => renderDropdownEntry(action, index))}</>
      </ButtonWithPopover>
      {showSupportRequestModal && <SupportRequestModal onClose={() => setShowSupportRequestModal(false)} />}
    </>
  );
};
